import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Plus, Search, MoreVertical, Key, Edit, Trash2, 
  Power, PowerOff, Copy, ExternalLink, Loader2, MessageSquare
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isApiKeyOpen, setIsApiKeyOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    webhook_url: '',
    rate_limit: 100
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/clients`, formData);
      toast.success('Cliente creado exitosamente');
      setIsCreateOpen(false);
      setFormData({ name: '', description: '', webhook_url: '', rate_limit: 100 });
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateClient = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/clients/${selectedClient.id}`, formData);
      toast.success('Cliente actualizado');
      setIsEditOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`¿Eliminar cliente "${client.name}"?`)) return;
    try {
      await axios.delete(`${API}/clients/${client.id}`);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleStatus = async (client) => {
    try {
      await axios.post(`${API}/clients/${client.id}/toggle-status`);
      toast.success(`Cliente ${client.is_active ? 'desactivado' : 'activado'}`);
      fetchClients();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleGenerateApiKey = async (client) => {
    try {
      const response = await axios.post(`${API}/clients/${client.id}/api-key`);
      setNewApiKey(response.data.api_key);
      setSelectedClient(client);
      setIsApiKeyOpen(true);
    } catch (error) {
      toast.error('Error al generar API Key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      description: client.description || '',
      webhook_url: client.webhook_url || '',
      rate_limit: client.rate_limit || 100
    });
    setIsEditOpen(true);
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0066CC]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#2C3E50]">Clientes</h1>
          <p className="text-[#6B7B8C] mt-1">Gestiona los sistemas que consumen la API</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="create-client-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#2C3E50]">Crear Nuevo Cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del cliente"
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descripción del cliente"
                  data-testid="client-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <Input
                  value={formData.webhook_url}
                  onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                  placeholder="https://api.cliente.com/webhook"
                  data-testid="client-webhook-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Límite de mensajes/hora</Label>
                <Input
                  type="number"
                  value={formData.rate_limit}
                  onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
                  data-testid="client-rate-limit-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
              <Button 
                onClick={handleCreateClient} 
                disabled={!formData.name || submitting}
                className="bg-[#0066CC] hover:bg-[#0052A3]"
                data-testid="create-client-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6B7B8C]" />
        <Input
          placeholder="Buscar clientes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="clients-search-input"
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="h-16 w-16 mx-auto text-[#E9EDEF] mb-4" />
          <p className="text-[#6B7B8C]">No hay clientes registrados</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <Card 
              key={client.id} 
              className={`card-hover bg-white border-0 shadow-sm ${!client.is_active ? 'opacity-60' : ''}`}
              data-testid={`client-card-${client.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${client.is_active ? 'bg-[#0066CC]/10' : 'bg-gray-100'}`}>
                      <Building2 className={`h-5 w-5 ${client.is_active ? 'text-[#0066CC]' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-[#2C3E50]">{client.name}</CardTitle>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        client.is_active 
                          ? 'bg-[#25D366]/10 text-[#25D366]' 
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {client.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`client-menu-${client.id}`}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleGenerateApiKey(client)}>
                        <Key className="h-4 w-4 mr-2" />
                        Generar API Key
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditDialog(client)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleStatus(client)}>
                        {client.is_active ? (
                          <>
                            <PowerOff className="h-4 w-4 mr-2" />
                            Desactivar
                          </>
                        ) : (
                          <>
                            <Power className="h-4 w-4 mr-2" />
                            Activar
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteClient(client)}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {client.description && (
                  <p className="text-sm text-[#6B7B8C] mb-4 line-clamp-2">{client.description}</p>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#6B7B8C]">Mensajes</p>
                    <p className="font-semibold text-[#2C3E50] flex items-center">
                      <MessageSquare className="h-4 w-4 mr-1 text-[#0066CC]" />
                      {client.message_count || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#6B7B8C]">Límite/hora</p>
                    <p className="font-semibold text-[#2C3E50]">{client.rate_limit || 100}</p>
                  </div>
                </div>
                {client.webhook_url && (
                  <div className="mt-4 p-2 bg-[#F0F2F5] rounded-lg flex items-center justify-between">
                    <span className="text-xs text-[#6B7B8C] truncate flex-1">{client.webhook_url}</span>
                    <ExternalLink className="h-3 w-3 text-[#6B7B8C] ml-2 flex-shrink-0" />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E50]">Editar Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <Input
                value={formData.webhook_url}
                onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Límite de mensajes/hora</Label>
              <Input
                type="number"
                value={formData.rate_limit}
                onChange={(e) => setFormData({ ...formData, rate_limit: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleUpdateClient} 
              disabled={!formData.name || submitting}
              className="bg-[#0066CC] hover:bg-[#0052A3]"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog open={isApiKeyOpen} onOpenChange={setIsApiKeyOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#2C3E50]">API Key Generada</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Importante:</strong> Esta es la única vez que verás esta API Key. Guárdala en un lugar seguro.
              </p>
            </div>
            <Label className="mb-2 block">API Key para {selectedClient?.name}</Label>
            <div className="flex items-center space-x-2">
              <Input
                value={newApiKey}
                readOnly
                className="font-mono text-sm"
                data-testid="generated-api-key"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => copyToClipboard(newApiKey)}
                data-testid="copy-api-key-btn"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsApiKeyOpen(false)} className="bg-[#0066CC] hover:bg-[#0052A3]">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
